import type {
  User,
  AddAuth,
  LoginParams,
  RegisterParams,
  LoginResponse,
  RsetPwdParams,
  Feedback,
  ChangeWechatBindParams,
  UserOauthBindStatus,
} from '@xianlaibang/shared/types'
import type { ApiUser, ApiAuth, ApiFinance, ApiBanned } from '@xianlaibang/shared/types/api'

/** 账号注销接口返回 data（与移动端一致） */
export interface AccountCancellationResult {
  stage: 'BALANCE_CONFIRM_REQUIRED' | 'SUBMITTED' | 'ALREADY_SUBMITTED' | string
  message?: string
  useMoney?: number | string
  supMoney?: number | string
  withdrawal?: number | string
  deposit?: number | string
  freeze?: number | string
}

/**
 * 统一为登录类接口附加 deviceInfo（如果调用方没有显式传入）
 */
function withDeviceInfo<T extends Record<string, any>>(payload: T): T {
  const deviceInfo = (payload as any).deviceInfo ?? useDeviceInfo().getDeviceInfo()
  return { ...payload, deviceInfo }
}

export const userApi = {
  // 用户注册
  register: (data: RegisterParams) => {
    const { post } = useHttp()
    return post<LoginResponse>('/register', withDeviceInfo(data))
  },

  // 密码登录
  login: (data: LoginParams) => {
    const { post } = useHttp()
    return post<LoginResponse>('/login', withDeviceInfo(data))
  },

  // 验证码登录
  verifyLogin: (data: LoginParams & { smsVerify: string }) => {
    const { post } = useHttp()
    return post<LoginResponse>('/phone', withDeviceInfo(data))
  },

  // 重置登录密码
  resetPassword: (data: RsetPwdParams) => {
    const { post } = useHttp()
    return post<LoginResponse>('/modifyPassword', withDeviceInfo(data))
  },

  // 登出
  logout: () => {
    return useHttp().delete('/logout')
  },

  // 获取用户信息
  getUserInfo: () => {
    const { get } = useHttp()
    return get<ApiUser>('/userinfo/user')
  },

  // 更新用户信息
  updateUser: (data: Partial<User>) => {
    const { put } = useHttp()
    return put<User>('/user/update', data)
  },

  // 添加用户认证信息
  addAuthInfo: (data: AddAuth) => {
    const { post } = useHttp()
    return post<ApiAuth>('/userinfo/auth', data)
  },

  // 更新用户认证信息
  updateAuthInfo: (data: AddAuth) => {
    const { put } = useHttp()
    return put<ApiAuth>('/userinfo/auth', data)
  },

  // 获取用户认证信息
  getAuthInfo: () => {
    const { get } = useHttp()
    return get<ApiAuth>('/userinfo/auth')
  },

  // 获取用户财务信息
  getFinanceInfo: () => {
    const { get } = useHttp()
    return get<ApiFinance>('/financial/user')
  },

  // 获取用户封禁信息
  getBannedInfo: (secretKeyCode: number, purchaseSupply: string) => {
    const { get } = useHttp()
    return get<ApiBanned>(`/userinfo/userBanned/${secretKeyCode}/${purchaseSupply}`)
  },

  // 获取用户是否设置交易密码
  getTradePassword: () => {
    const { get } = useHttp()
    return get<boolean>('/financial/user/isPassword')
  },

  // 重置交易密码
  resetTradePassword: (data: RsetPwdParams) => {
    const { post } = useHttp()
    return post<boolean>('/modifyWithdrawPassword', data)
  },

  // 意见反馈
  addFeedback: (data: Feedback) => {
    const { post } = useHttp()
    return post<boolean>('/system/feedback/submit', data)
  },

  // 获取意见反馈列表
  getFeedbackList: (query: Record<string, any>) => {
    const { get } = useHttp()
    return get<null, Feedback>('/system/feedback/info', query)
  },

  // 获取电子签二维码
  signNameImg: (query: Record<string, any>) => {
    const { get } = useHttp()
    return get<string>('/recycle/recycleOrderContractSigning/createqrcode', query)
  },

  // 获取实名认证二维码
  realNameImg: (query: Record<string, any>) => {
    const { get } = useHttp()
    return get<string>('/userinfo/auth/createqrcode', query)
  },

  // 换绑微信（对齐小程序 editWechat → PUT /changeBing）
  changeWechatBind: (data: ChangeWechatBindParams) => {
    const { put } = useHttp()
    return put<boolean>('/changeBing', data)
  },

  // PC 微信网站应用授权链接
  getWechatWebLoginUrl: () => {
    const { get } = useHttp()
    return get<string>('/wechatWebLogin')
  },

  // 第三方绑定状态 platform: 0=微信小程序
  getWechatBindStatus: (platform = 0) => {
    const { get } = useHttp()
    return get<UserOauthBindStatus>(`/userBindStatus/${platform}`)
  },

  // 提交账号注销申请（与移动端 applyAccountCancellation 一致）
  applyAccountCancellation: (data: { confirmBalance: boolean }) => {
    const { post } = useHttp()
    return post<AccountCancellationResult>('/system/api/accountCancellation', data)
  },
}

// SSR 场景获取用户信息
export function useUserInfo() {
  return useRequest<User>('/user/info')
}
